import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useParams } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import type { Invoice } from '../../../types';
import AdminBillingPage from './AdminBillingPage';
import '../styles/AdminBillingPage.css';

function dateText(value: any): string {
  if (!value) return '—';
  if (typeof value === 'string') return value.slice(0, 10);
  const date = value?.toDate?.();
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '—';
}

function money(cents: unknown, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
}

function LegacyInvoiceView({ invoice }: { invoice: Invoice }): React.ReactElement {
  const fields = [
    ['Partner / school', invoice.schoolNameSnapshot || invoice.payerLegalNameSnapshot || '—'],
    ['Season', invoice.schoolTermSnapshot || invoice.termId || '—'],
    ['Amount', money(invoice.totalCents ?? invoice.subtotalCents, invoice.currency || 'USD')],
    ['Status', String(invoice.status || 'draft').replace(/_/g, ' ')],
    ['Sent date', dateText((invoice as any).sentAt || invoice.invoiceDate)],
    ['Due date', dateText(invoice.paymentDueDate)],
    ['Paid date', dateText((invoice as any).paidAt)],
    ['Invoice number', invoice.internalInvoiceNumberSnapshot || '—'],
    ['Service period', invoice.servicePeriodStart || invoice.servicePeriodEnd ? `${invoice.servicePeriodStart || '—'} – ${invoice.servicePeriodEnd || '—'}` : '—'],
  ];
  return <main className="billing-page legacy-invoice-view">
    <div className="billing-page-header">
      <Link className="btn btn-secondary" to="/app/admin" state={{ adminTab: 'invoicing' }}>← Invoices</Link>
      <div><h1>Legacy invoice</h1><p>Read-only snapshot. This invoice is excluded from current invoicing workflows.</p></div>
    </div>
    <div className="billing-card">
      <dl className="legacy-invoice-fields">{fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
      {invoice.pdf?.url ? <a className="btn btn-primary" href={invoice.pdf.url} target="_blank" rel="noreferrer">Open stored PDF</a> : <p className="muted">No PDF was stored for this invoice.</p>}
    </div>
  </main>;
}

export default function InvoiceDetailRouter(): React.ReactElement {
  const { invoiceId } = useParams();
  const id = String(invoiceId || '').trim();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!id) { setError('Invoice ID is missing.'); setLoading(false); return; }
    getDoc(doc(db, 'invoices', id))
      .then((snapshot) => snapshot.exists() ? setInvoice({ id: snapshot.id, ...(snapshot.data() as any) } as Invoice) : setError('Invoice not found.'))
      .catch((nextError) => setError(nextError instanceof Error ? nextError.message : 'Could not load invoice.'))
      .finally(() => setLoading(false));
  }, [id]);
  if (loading) return <main className="billing-page"><p>Loading invoice…</p></main>;
  if (error || !invoice) return <main className="billing-page"><div className="admin-invoices-error">{error || 'Invoice not found.'}</div></main>;
  if ((invoice as any).legacy === true || !String((invoice as any).partnerId || '').trim()) return <LegacyInvoiceView invoice={invoice} />;
  return <AdminBillingPage />;
}
